import * as z from "zod/v4";
import type { WorldModelTheory } from "./world-model";
import type { QuantitativeStudy } from "./quantitative-studies";

export const beliefExperimentId = z.string().regex(/^owner-exp-[a-f0-9]{48}$/);
export const beliefExperimentInput = z.object({
  idempotencyKey: z.uuid(),
  theoryId: z.string().regex(/^world-theory-[a-f0-9-]{36}$/),
  expectedVersion: z.number().int().positive(),
  adapter: z.literal("binary-transfer-v1"),
  observableGroup: z.enum(["context", "price", "activity"]),
  question: z.string().trim().min(20).max(1500),
  interpretation: z.string().trim().min(30).max(4000),
  retryOf: beliefExperimentId.optional(),
}).strict();
export type BeliefExperimentInput = z.infer<typeof beliefExperimentInput>;
export const beliefExperimentAdapters = [{
  id: "binary-transfer-v1", name: "Prediction-market transfer", version: 1,
  target: "Resolved binary event outcome", unit: "Probability, 0–1",
  scope: "Registered Kalshi historical cohort, July–September 2026. Previously inspected discovery data.",
  method: "Compare market probability, regularized calibration, and calibration with one added observable group.",
  validation: "Later periods, unseen series, and unseen categories; event-disjoint chronological fits.",
  groups: [
    { id: "context", name: "Shared context", detail: "Venue topics, settlement-source hosts, and event frequency. These are context proxies, not measured causal mechanisms." },
    { id: "price", name: "Price history", detail: "Completed price momentum, volatility, spread, and neighboring-contract prices." },
    { id: "activity", name: "Market activity", detail: "Past volume, open interest, market age, and time since trading." },
  ],
  limitations: ["This adapter tests the declared observable interpretation, not every claim in a belief.", "Reusing this historical cohort does not create independent evidence or establish profitability.", "Macroeconomic, physical, options-payoff, and causal-intervention hypotheses require their own registered adapters."],
}] as const;

export type ExperimentObject = { bucket: string; name: string; generation: string; bytes: number; sha256: string };
export type BeliefExperimentRequest = {
  schemaVersion: 1; id: string; environment: "development" | "production"; createdAt: string;
  input: BeliefExperimentInput; theory: WorldModelTheory; theorySha256: string;
  mode: "retrospective_discovery"; liveExecution: false;
};
export type BeliefExperimentRecord = {
  schemaVersion: 1; id: string; environment: "development" | "production";
  title: string; theoryId: string; theoryVersion: number; theorySha256: string; inputSha256: string;
  question: string; adapter: "binary-transfer-v1"; observableGroup: "context" | "price" | "activity";
  status: "queued" | "running" | "completed" | "failed";
  stage: string; createdAt: string; updatedAt: string; startedAt: string | null; completedAt: string | null;
  requestObject: ExperimentObject; studyObject: ExperimentObject | null;
  execution: string | null; dispatchAttempts: number; dispatchedAt: string | null;
  dispatchNotice: string | null; error: string | null; retryOf: string | null;
};
export type BeliefExperimentDetail = { record: BeliefExperimentRecord; request: BeliefExperimentRequest; study: QuantitativeStudy | null };
export class BeliefExperimentError extends Error {
  constructor(message: string, public status = 409) { super(message); }
}
